import { useState, useEffect } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useToast } from "@/components/ui/use-toast"
import { PlaybookTemplate } from "@/types/playbooks"
import { playbookService } from "@/services/playbooks"
import { ArrowLeft, Play } from "lucide-react"
import { supabase } from "@/lib/supabase"

export default function NewPlaybookRun() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()
  const [templates, setTemplates] = useState<PlaybookTemplate[]>([])
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>("")
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [subscriptions, setSubscriptions] = useState<{id: string, name: string}[]>([])
  const [selectedSubscriptionId, setSelectedSubscriptionId] = useState<string>("")
  const [isLoadingSubscriptions, setIsLoadingSubscriptions] = useState(true)

  // Load available templates
  useEffect(() => {
    const loadTemplates = async () => {
      try {
        const data = await playbookService.templates.getBySubscription()
        setTemplates(data)
        
        // If there's a templateId in the URL, select it automatically
        const templateId = searchParams.get('templateId')
        if (templateId) {
          setSelectedTemplateId(templateId)
        }
      } catch (error) {
        console.error("Error loading templates:", error)
        toast({
          title: "Erro ao carregar templates",
          description: "Não foi possível carregar os templates disponíveis.",
          variant: "destructive"
        })
      } finally {
        setIsLoading(false)
      }
    }

    loadTemplates()
  }, [toast, searchParams])

  // Load available subscriptions
  useEffect(() => {
    const loadSubscriptions = async () => {
      setIsLoadingSubscriptions(true)
      try {
        const { data, error } = await supabase
          .from('subscriptions')
          .select('id')
          .order('id')
        
        if (error) throw error
        
        // Transform data to match UI requirements
        const formattedSubscriptions = (data || []).map(sub => ({
          id: sub.id,
          name: `Subscription ${sub.id.substring(0, 8)}...` // Use partial ID as display name
        }))
        
        setSubscriptions(formattedSubscriptions)
        
        // If there's only one subscription, select it automatically
        if (data && data.length === 1) {
          setSelectedSubscriptionId(data[0].id)
        }
      } catch (error) {
        console.error("Error loading subscriptions:", error)
        toast({
          title: "Erro ao carregar assinaturas",
          description: "Não foi possível carregar as assinaturas disponíveis.",
          variant: "destructive"
        })
      } finally {
        setIsLoadingSubscriptions(false)
      }
    }

    loadSubscriptions()
  }, [toast])

  const handleStartRun = async () => {
    if (!selectedTemplateId || !selectedSubscriptionId) {
      toast({
        title: "Informações Necessárias",
        description: "Selecione um template e uma assinatura para iniciar o Playbook.",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsSubmitting(true);

      // Obter o usuário atual da sessão
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session?.user) {
        toast({
          title: "Erro de Autenticação",
          description: "Você precisa estar autenticado para criar um playbook.",
          variant: "destructive"
        });
        return;
      }

      // Encontrar os objetos completos do template e subscription selecionados
      const templateObj = templates.find(t => t.id === selectedTemplateId);
      const subscriptionObj = subscriptions.find(s => s.id === selectedSubscriptionId);
      
      if (!templateObj || !subscriptionObj) {
        throw new Error("Template ou assinatura não encontrados");
      }

      // 1. Criar o playbook run no banco de dados
      const { data: newPlaybookRun, error: runError } = await supabase
        .from('playbook_runs')
        .insert({
          name: name || templateObj.name,
          description: description || templateObj.description,
          playbook_template_id: templateObj.id,
          subscription_id: subscriptionObj.id,
          status: 'running',
          scheduled_start: new Date().toISOString(),
          started_at: new Date().toISOString(),
          run_type: 'on-demand',
          created_by: session.user.id,
          updated_by: session.user.id
        })
        .select()
        .single();

      if (runError) {
        console.error("Erro ao criar playbook run:", runError);
        throw runError;
      }

      console.log("Playbook run criado:", newPlaybookRun);

      // 2. Buscar as tarefas do template
      const { data: templateTaskGroups, error: groupsError } = await supabase
        .from('playbook_template_task_groups')
        .select(`
          id,
          title,
          icon,
          tasks:playbook_template_tasks(
            id,
            title,
            description,
            task_type,
            assignee_type,
            assignees,
            deadline_type,
            deadline_value,
            deadline_reference,
            detailed_description,
            custom_fields,
            task_order
          )
        `)
        .eq('playbook_template_id', templateObj.id)
        .order('group_order', { ascending: true });

      if (groupsError) {
        console.error("Erro ao buscar grupos de tarefas:", groupsError);
        throw groupsError;
      }

      // 3. Criar as tarefas para o playbook run com o cálculo correto de due_date
      let allTasks = [];
      
      if (templateTaskGroups && templateTaskGroups.length > 0) {
        // Extrair todas as tarefas de todos os grupos
        templateTaskGroups.forEach(group => {
          if (group.tasks && group.tasks.length > 0) {
            group.tasks.forEach(task => {
              allTasks.push({
                ...task,
                group_id: group.id
              });
            });
          }
        });
        
        // Preparar as tarefas para inserção
        const tasksToInsert = allTasks.map(templateTask => {
          // Calcular due_date com base em deadline_value e deadline_type
          let dueDate = null;
          
          if (templateTask.deadline_value) {
            const currentDate = new Date();
            // Adicionar o número de dias definido em deadline_value
            currentDate.setDate(currentDate.getDate() + templateTask.deadline_value);
            dueDate = currentDate.toISOString();
          } else if (templateTask.default_duration_days && newPlaybookRun.scheduled_start) {
            // Usar a lógica antiga como fallback
            const startDate = new Date(newPlaybookRun.scheduled_start);
            startDate.setDate(startDate.getDate() + templateTask.default_duration_days);
            dueDate = startDate.toISOString();
          }

          return {
            playbook_run_id: newPlaybookRun.id,
            playbook_template_group_id: templateTask.group_id,
            title: templateTask.title,
            task_type: templateTask.task_type || 'task',
            description: templateTask.description,
            detailed_description: templateTask.detailed_description,
            status: templateTask.task_order === 1 ? 'current' : 'upcoming',
            task_order: templateTask.task_order,
            due_date: dueDate,
            assignee_type: "fixed",
            assignees: templateTask.assignees || null,
            deadline_type: templateTask.deadline_type || 'days',
            deadline_value: templateTask.deadline_value || 2,
            deadline_reference: templateTask.deadline_reference || 'launch',
            custom_fields: templateTask.custom_fields || null,
            created_by: session.user.id,
            updated_by: session.user.id
          };
        });

        // Inserir as tarefas no banco de dados
        if (tasksToInsert.length > 0) {
          const { error: insertError } = await supabase
            .from('playbook_tasks')
            .insert(tasksToInsert);

          if (insertError) {
            console.error("Erro ao criar tarefas do playbook:", insertError);
            toast({
              title: "Aviso",
              description: "Playbook criado, mas ocorreu um erro ao criar as tarefas.",
              variant: "destructive"
            });
          }
        }
      }

      toast({
        title: "Playbook Iniciado",
        description: "O Playbook foi iniciado com sucesso!",
      });

      // Navegar para a página de detalhes do playbook
      navigate(`/customer-success/playbooks/runs/${newPlaybookRun.id}`);
    } catch (error) {
      console.error("Erro ao iniciar playbook:", error);
      toast({
        title: "Erro ao Iniciar Playbook",
        description: "Ocorreu um erro ao iniciar o playbook. Tente novamente.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Check if still loading data
  const isPageLoading = isLoading || isLoadingSubscriptions
  
  if (isPageLoading) {
    return (
      <div className="w-full px-6 py-6">
        <div className="flex items-center justify-center h-64">
          <p className="text-muted-foreground">Carregando dados...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="w-full px-6 py-6">
      {/* Header and actions */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-extrabold">Iniciar Playbook</h1>
          <p className="text-muted-foreground">
            Selecione um template e configure as informações iniciais para iniciar a execução.
          </p>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Button>
      </div>

      {/* Configuration card */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Selecione um Template</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="template">Template de Playbook</Label>
            <Select 
              value={selectedTemplateId} 
              onValueChange={setSelectedTemplateId}
            >
              <SelectTrigger id="template">
                <SelectValue placeholder="Selecione um template" />
              </SelectTrigger>
              <SelectContent>
                {templates.map((template) => (
                  <SelectItem key={template.id} value={template.id}>
                    {template.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subscription">Assinatura</Label>
            <Select 
              value={selectedSubscriptionId} 
              onValueChange={setSelectedSubscriptionId}
            >
              <SelectTrigger id="subscription">
                <SelectValue placeholder="Selecione uma assinatura" />
              </SelectTrigger>
              <SelectContent>
                {subscriptions.map((subscription) => (
                  <SelectItem key={subscription.id} value={subscription.id}>
                    {subscription.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="name">Nome do Playbook (opcional)</Label>
            <Input 
              id="name" 
              placeholder="Nome personalizado"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Deixe em branco para usar o nome do template.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">Descrição (opcional)</Label>
            <Input 
              id="description" 
              placeholder="Descrição personalizada"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Deixe em branco para usar a descrição do template.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Action button */}
      <div className="flex justify-end">
        <Button 
          onClick={handleStartRun} 
          disabled={!selectedTemplateId || !selectedSubscriptionId || isSubmitting}
        >
          {isSubmitting ? (
            <div className="animate-spin h-4 w-4 mr-2 border-2 border-b-transparent rounded-full"></div>
          ) : (
            <Play className="h-4 w-4 mr-2" />
          )}
          Iniciar Playbook
        </Button>
      </div>
    </div>
  )
} 